
import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedModule } from '../@commons/ui-components/shared.module';
import { ArticleComponent } from '../@commons/@components/article/article.component';
import { HeaderHomeComponent } from './@components/header-home/header-home.component';
import { fadeIn } from '../@commons/animations/animations';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import routesConstants from '../@commons/constants/routes.constants';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { StorageService } from '../@commons/@services/front/storage.service';
import { UserApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/user/user-api';
import { User } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@entities/app/user.entity';
import { AddressToUserApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/address-to-user/address-to-user-api';
import { UserResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/user/user.response';
import { AddressToUserListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/address-to-user-list.response';
import { PagingResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/pagination/paging.response';
import { HeaderPaddingSafeAreaComponent } from '../@commons/@components/header-padding-safe-area/header-padding-safe-area.component';
import { SkeletonLoaderComponent } from '../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';
import { ArticleApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/article/article-api';
import {
  ArticleListRequest
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/article/article-list.request';
import { PosthogService } from '../@commons/@services/front/posthog.service';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import { GddMilestonesWidgetComponent, GddMilestoneItem } from './@components/gdd-milestones-widget/gdd-milestones-widget.component';
import { stepClock } from '../@commons/utils/step-clock.util';
import { displayedBadge } from '../@commons/@components/gdd-badge/gdd-badge.component';
import { GreenLightApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/green-light/green-light-api';
import { GreenLightVerdictResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/green-light/green-light.response';
import { AdviceApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/advice/advice-api';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { AdviceTodayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/advice/advice-today.response';
import { UserAlertResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/smart-alerts/user-alert.response';
import { PremiumTeaserComponent } from '../@commons/ui-components/premium-teaser/premium-teaser.component';
import { PremiumToastComponent } from '../@commons/@toasts/premium-toast/premium-toast.component';
import { PremiumService } from '../@commons/@services/front/premium.service';
import { StateComponent } from '../@commons/ui-components/state/state.component';
import { OfflineBannerComponent } from '../@commons/ui-components/state/offline-banner.component';
import { DataStore, SNAPSHOT_ONLY } from '../@commons/@services/data/data-store.service';
import { AlertsStore } from '../@commons/@services/data/alerts.store';
import { GardenTodo, GardenTodoStore } from '../@commons/@services/data/garden-todo.store';
import { FinishedPlantsComponent } from './@components/finished-plants/finished-plants.component';
import { isGreenLightHomePeriod } from '../green-light/green-light.util';
import { LOADING, LoadState, errorState } from '../@commons/@services/data/load-state';
import { GardenStore } from '../@commons/@services/data/garden.store';
import { GardenRankService } from '../@commons/@services/front/garden-rank.service';
import { WillyGreetingService } from '../@commons/@services/front/willy-greeting.service';
import { AdviceReplyStore } from '../@commons/@services/data/advice-reply.store';
import { SetupStep, SetupStepService } from './@components/first-launch/setup-step.service';
import { labelText } from '../@commons/utils/label-text.util';
import { formatVwDate } from '../@commons/pipes/vw-date.pipe';
import { AppConfigService } from '../@commons/@services/front/app-config.service';
import { CloseIconComponent } from '../@icons/close-icon.component';
import { ListRowComponent } from '../@commons/ui-components/list-row/list-row.component';
import { ListSectionComponent } from '../@commons/ui-components/list-row/list-section.component';
import { DayBoardComponent } from './@components/day-board/day-board.component';
import { FirstLaunchComponent, FirstLaunchStep } from './@components/first-launch/first-launch.component';
import { GuestTrialComponent } from './@components/guest-trial/guest-trial.component';
import { WeatherMoonBandComponent } from './@components/weather-moon-band/weather-moon-band.component';
import {
  BandMoon,
  BandWeather,
  WeatherCondition,
  bandMoonFromLunar,
  bandWeatherFromForecast,
} from './@components/weather-moon-band/weather-moon-band.model';
import { DayMessageHistoryService } from './@components/day-message/day-message-history.service';
import { localizedZoneName } from '../@commons/pipes/zone-name.pipe';
import { chooseDayMessages } from './@components/day-message/day-message.engine';
import { DayChoice, DayContext, isWeatherFresh, localDay } from './@components/day-message/day-message.model';

/**
 * Ce que l'accueil charge, et pourquoi si peu.
 *
 * L'accueil est un briefing : il montre le dessus de la pile et renvoie aux
 * ecrans specialises pour le reste. Charger vingt articles pour en lire deux,
 * ou cinquante plantes pour en resumer trois, se paie a chaque ouverture.
 */
const HOME_ARTICLES = 3;
const HOME_GDD_ITEMS = 3;
/** « Plus tard » sur le premier lancement : le jour où il a été rangé. */
export const FIRST_LAUNCH_LATER_KEY = 'first-launch-later';

/**
 * Ce que l'accueil lit du compte : son identifiant et sa langue. C'est aussi
 * tout ce qui en est gardé pour le hors ligne (ni nom, ni e-mail, ni
 * téléphone sur le téléphone, NA-05).
 */
export type HomeUser = Pick<User, 'uid' | 'preferredLanguage'>;

interface HomeAccount {
  user: HomeUser;
  /** Première adresse du compte ; sans elle, l'onboarding n'est pas fini. */
  addressUid: string | null;
}

@Component({
  selector: 'vw-home', // Indicates that this component is standalone
  imports: [
    SharedModule,
    ArticleComponent,
    HeaderHomeComponent,
    TranslatePipe,
    HeaderPaddingSafeAreaComponent,
    SkeletonLoaderComponent,
    GddMilestonesWidgetComponent,
    PremiumTeaserComponent,
    PremiumToastComponent,
    StateComponent,
    OfflineBannerComponent,
    FinishedPlantsComponent,
    DayBoardComponent,
    FirstLaunchComponent,
    GuestTrialComponent,
    WeatherMoonBandComponent,
    CloseIconComponent,
    ListRowComponent,
    ListSectionComponent,
], // Imports shared and common modules
  templateUrl: './home.component.html', // Path to the component's template
  styleUrls: ['./home.component.scss'], // Path to the component's styles
  animations: [fadeIn],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: ArticleApi,
      useClass: ArticleApi,
    },
    {
      provide: UserApi,
      useClass: UserApi,
    },
    {
      provide: AddressToUserApi,
      useClass: AddressToUserApi,
    },
    {
      provide: PlantApi,
      useClass: PlantApi,
    },
    {
      provide: GreenLightApi,
      useClass: GreenLightApi,
    },
    {
      provide: AdviceApi,
      useClass: AdviceApi,
    },
  ]
})
export class HomeComponent implements OnInit {
  articles: any[] = []; // Array to hold badge information
  gddMilestones: GddMilestoneItem[] = []; // In-progress plants with GDD/thermal status
  /** Conseils, météo des jours qui viennent, lune et réponses du jour : un seul appel. */
  advice: AdviceTodayResponse | null = null;
  /** Alertes non lues : celles du jour deviennent des messages. */
  unreadAlerts: UserAlertResponse[] = [];
  /** Verdicts Feu vert du compte (à partir du 15 mars, K22). */
  greenLights: GreenLightVerdictResponse[] = [];
  /** Les gestes du jardin et ses plantes (null : pas chargés). */
  todo: GardenTodo | null = null;
  /** Sachets, plantes et zones du compte : le premier lancement et les premiers pas en dépendent. */
  counts: { seeds: number; plants: number; zones: number } | null = null;
  /** Les zones de l'adresse, nommées : « De la place sur Planche 2 ». */
  zones: { uid: string; name: string }[] = [];
  /** Le mot du jour, choisi une fois tout chargé. */
  choice: DayChoice | null = null;
  user: HomeUser;
  userAddressUid: string;
  /** Le compte et son adresse : sans eux, rien ne s'affiche que le squelette ou la panne. */
  pageState: LoadState = LOADING;
  /** « Plus tard » sur le premier lancement : rangé pour aujourd'hui. */
  firstLaunchLater = false;
  readonly gddMaxItems = HOME_GDD_ITEMS;

  /**
   * Un invité n'a ni jardin ni conseils : la route lui est fermée côté serveur.
   *
   * On lit le jeton, comme le fait déjà le chatbot — c'est instantané et
   * gratuit. Appeler quand même produisait un 403 à chaque ouverture, dans le
   * journal d'incidents de production : une porte fermée dont on connaissait
   * déjà le verrou.
   */
  private readonly authApi = new AuthApi();

  readonly premiumService = inject(PremiumService);
  private readonly gardenRank = inject(GardenRankService);
  private readonly greenLightApi = inject(GreenLightApi);
  private readonly gardenStore = inject(GardenStore);
  private readonly alertsStore = inject(AlertsStore);
  private readonly todoStore = inject(GardenTodoStore);
  private readonly dataStore = inject(DataStore);
  private readonly history = inject(DayMessageHistoryService);
  private readonly willyGreeting = inject(WillyGreetingService);
  private readonly replies = inject(AdviceReplyStore);
  private readonly setupStep = inject(SetupStepService);
  /** Mise à jour de l'app recommandée : une ligne en tête, refermable (NA-03). */
  readonly appConfig = inject(AppConfigService);
  premiumPaywallMode: boolean = false;
  /** D'où vient l'ouverture du paywall, pour savoir quel encart convertit. */
  paywallSource: string = 'home';
  /** Chaque `/home?finished=1` (push de rangement de saison) demande d'ouvrir la liste des cultures finies. */
  finishedOpenRequest = 0;

  /**
   * L'accroche Premium n'apparaît que sur une surface où la valeur vient
   * d'être DÉMONTRÉE, une fois les gestes dits. Jamais en interstitiel, jamais
   * pour un abonné, ni avant que le potager soit configuré.
   */
  get showTeasers(): boolean {
    // `ready()` est indispensable : avant l'hydratation du jeton, `isPremium()`
    // vaut `false` par DÉFAUT et non par constat. Sans ce garde-fou, un abonné
    // voit l'offre payante clignoter à chaque ouverture de l'app. Premium
    // fermé, `hasPremiumAccess()` est vrai pour tous : aucun encart.
    return this.premiumService.ready() && !this.premiumService.hasPremiumAccess() && !this.firstLaunch;
  }

  openPaywall(source: string): void {
    this.paywallSource = source;
    this.premiumPaywallMode = true;
  }

  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    private userApi: UserApi,
    private addressToUserApi: AddressToUserApi,
    private storageService: StorageService,
    private translate: TranslateService,
    private articleApi: ArticleApi,
    private posthogService: PosthogService,
    private plantApi: PlantApi,
    private adviceApi: AdviceApi,
  ) { }

  async ngOnInit() {
    // L'observable et non l'instantané : un push touché pendant que l'accueil
    // est affiché le réutilise sans le recréer.
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => this.openFromLink(params));
    this.firstLaunchLater = this.readLater();
    this.setupStep.refresh();
    await this.load();
  }

  /**
   * Les liens d'ouverture de l'accueil (notification, e-mail) :
   * `?paywall=trial_ending`, rappels de fin d'essai ; `?finished=1`, push de
   * rangement de saison. Ouvert, le paramètre quitte l'adresse : un retour
   * arrière ne rejoue rien, et le même lien rouvre quand il revient.
   */
  private openFromLink(params: ParamMap) {
    const paywall = params.get('paywall') === 'trial_ending';
    const finished = params.get('finished') === '1';
    if (!paywall && !finished) return;
    if (paywall) this.openPaywall('trial_ending');
    if (finished) this.finishedOpenRequest++;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { paywall: null, finished: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Le compte est chargé : l'adresse est posée, les blocs peuvent partir. */
  get userLoaded(): boolean {
    return this.pageState.status === 'ready';
  }

  get isGuest(): boolean {
    return this.authApi.isGuest();
  }

  /**
   * Charge l'accueil ; rappelé tel quel par « Réessayer ».
   *
   * Le compte d'abord (il pose l'adresse que lisent la météo et les
   * conseils), puis tous les blocs ensemble : l'accueil attend le plus long
   * d'entre eux, plus leur somme (ARC-09). Chaque bloc échoue seul ; seule la
   * panne du compte met tout l'écran en erreur. Le mot du jour se choisit
   * ensuite, sur ce qui a été chargé.
   */
  async load() {
    this.pageState = LOADING;
    try {
      await this.loadUser();
      this.pageState = { status: 'ready' };
    } catch (e) {
      console.error('HomeComponent: failed to initialize', e);
      this.pageState = errorState(e);
      return;
    }
    await Promise.allSettled([
      this.loadGddMilestones(),
      this.loadAdvice(), // le mot du jour, la météo et la lune
      this.loadAlerts(),
      this.loadGreenLights(),
      this.loadTodo(),
      this.loadCounts(),
      this.loadArticles(),
    ]);
    this.chooseDayMessage();
  }

  /**
   * Les articles ne font pas l'accueil : leur échec masque la section, il ne
   * met pas tout l'écran en erreur.
   */
  async loadArticles() {
    try {
      const articles = await this.articleApi.list(new ArticleListRequest(), 1, HOME_ARTICLES, this.storageService.getCurrentLanguage());
      this.articles = articles.results;
    } catch (e) {
      console.warn('HomeComponent: failed to load articles', e);
    }
  }

  /** Le compte et son adresse ; hors ligne, ceux du dernier chargement réussi. */
  async loadUser() {
    const account = await this.dataStore.get('user/home', () => this.fetchAccount(), SNAPSHOT_ONLY);
    this.user = account.user;
    if (account.addressUid) {
      this.userAddressUid = account.addressUid;
      this.userApi.addUserAddressUid(this.userAddressUid);
    }

    if (this.user && this.user.preferredLanguage) {
      this.storageService.setCurrentLang(
        this.user.preferredLanguage.toUpperCase()
      );
      this.translate.use(this.user.preferredLanguage.toLowerCase());
    }
    this.posthogService.identify(this.user.uid, {
      language: this.user.preferredLanguage,
    });
  }

  private async fetchAccount(): Promise<HomeAccount> {
    const userResponse: UserResponse = await this.userApi.getCurrentUser();
    const addressToUserPaging: PagingResponse<AddressToUserListResponse> =
      await this.addressToUserApi.list(
        {
          userUid: userResponse.uid,
        },
        0,
        10,
        this.storageService.getCurrentLanguage()
      );
    const { uid, preferredLanguage } = userResponse;
    return {
      user: { uid, preferredLanguage },
      addressUid: addressToUserPaging.results[0]?.addressUid ?? null,
    };
  }

  /**
   * Un seul appel pour le mot du jour, la bande météo et lune et les réponses
   * du jour. Les réponses données hors ligne partent d'abord : le serveur les
   * retire alors de lui-même.
   */
  async loadAdvice() {
    if (this.authApi.isGuest()) {
      return;
    }
    await this.replies.flush().catch(() => undefined);
    try {
      const lang = this.storageService.getCurrentLanguage();
      this.advice = await this.dataStore.get(`garden/today:${lang}`, () => this.adviceApi.getToday(lang), SNAPSHOT_ONLY);
    } catch (e) {
      console.warn('HomeComponent: failed to load garden advice', e);
      this.advice = null;
    }
  }

  /** Les alertes non lues ; celles du jour qui demandent un geste deviennent des messages. Best-effort. */
  async loadAlerts() {
    if (this.authApi.isGuest()) {
      return;
    }
    try {
      this.unreadAlerts = await this.alertsStore.unread();
    } catch (e) {
      console.warn('HomeComponent: failed to load alerts', e);
    }
  }

  /** Les gestes du jardin et ses plantes. Best-effort : une panne retire leurs messages, pas l'accueil. */
  async loadTodo() {
    if (this.authApi.isGuest()) {
      return;
    }
    try {
      this.todo = await this.todoStore.load();
    } catch (e) {
      console.warn('HomeComponent: failed to load garden tasks', e);
    }
  }

  /**
   * Feu vert (NF-2), à partir du 15 mars (K22). Avant cette date, aucun
   * appel. Une panne de l'API ne retire que ses messages.
   */
  async loadGreenLights(today: Date = new Date()) {
    if (this.authApi.isGuest() || !isGreenLightHomePeriod(today)) {
      return;
    }
    try {
      const response = await this.greenLightApi.list(this.storageService.getCurrentLanguage());
      this.greenLights = response?.items ?? [];
    } catch (e) {
      console.warn('HomeComponent: failed to load green lights', e);
      this.greenLights = [];
    }
  }

  /** Sachets, plantes (GardenRankService) et zones de l'adresse. Illisibles : ni premier lancement ni premiers pas. */
  async loadCounts() {
    if (this.authApi.isGuest()) {
      return;
    }
    try {
      const [count, zones] = await Promise.all([this.gardenRank.count(), this.gardenStore.zones(this.userAddressUid)]);
      this.counts = count ? { seeds: count.seeds, plants: count.plants, zones: zones.length } : null;
      this.zones = zones.map((zone) => ({ uid: zone.uid, name: localizedZoneName(zone, this.translate) }));
    } catch (e) {
      console.warn('HomeComponent: failed to load garden totals', e);
      this.counts = null;
    }
  }

  /** Le mot du jour sur ce que l'accueil a chargé, noté dans l'historique de l'appareil ; Willy salue à la première ouverture du jour. */
  private chooseDayMessage(now: Date = new Date()) {
    if (this.authApi.isGuest()) return;
    const ctx: DayContext = {
      now,
      lang: (this.storageService.getCurrentLanguage() || 'fr').toLowerCase(),
      advice: this.advice,
      alerts: this.unreadAlerts,
      greenLights: this.greenLights,
      plantActions: this.todo?.plantActions ?? [],
      plants: this.todo?.plants?.filter((p) => !p.endedAt) ?? null,
      thermal: this.gddMilestones.map((m) => ({
        plantUid: m.plantUid,
        cultivationName: m.cultivationName,
        locationName: m.locationName ?? '',
        conditionBadge: m.conditionBadge ?? null,
      })),
      counts: this.counts,
      zones: this.zones,
      pendingReplies: this.replies.queuedFor(localDay(now)),
      setup: this.setup,
    };
    this.choice = chooseDayMessages(ctx, this.history.entries(), undefined, this.history.shownOnce());
    this.history.record(localDay(now), this.choice);
    this.willyGreeting.greetIfFirstToday(now);
  }

  /** Compte créé, potager pas encore configuré : ni zone, ni plante, ni sachet. */
  get firstLaunch(): boolean {
    return !!this.counts && this.counts.zones === 0 && this.counts.plants === 0 && this.counts.seeds === 0;
  }

  /** L'étape en plus (alertes dans l'app, l'app dans le navigateur), sans charge. */
  get setup(): SetupStep | null {
    return this.setupStep.current();
  }

  get firstLaunchDone(): Record<FirstLaunchStep, boolean> {
    return { commune: !!this.userAddressUid, zone: (this.counts?.zones ?? 0) > 0, plants: false };
  }

  /** « Plus tard » : l'accueil du premier lancement revient demain. */
  onFirstLaunchLater() {
    this.firstLaunchLater = true;
    try {
      this.storageService.setItem(FIRST_LAUNCH_LATER_KEY, localDay(new Date()));
    } catch {
      // Sans stockage, le choix tient le temps de l'écran.
    }
  }

  private readLater(): boolean {
    try {
      return this.storageService.getItem(FIRST_LAUNCH_LATER_KEY) === localDay(new Date());
    } catch {
      return false;
    }
  }

  /** La météo de la bande : aujourd'hui et les trois jours suivants ; ancienne ou absente, la bande le dit. */
  get bandWeather(): BandWeather | null {
    if (!this.advice) return null;
    const fresh = isWeatherFresh(this.advice, new Date());
    return fresh || this.advice.forecastAt ? bandWeatherFromForecast(this.advice.forecast) : null;
  }

  get bandStale(): Date | null {
    if (!this.advice || isWeatherFresh(this.advice, new Date())) return null;
    return this.advice.forecastAt ? new Date(this.advice.forecastAt) : null;
  }

  get bandMoon(): BandMoon | null {
    return bandMoonFromLunar(this.advice?.lunar);
  }

  /** La bande s'affiche dès qu'il y a de la météo ou une lune ; sans commune, elle attend. */
  get showBand(): boolean {
    return !!this.advice && !!this.userAddressUid && (!!this.advice.forecast?.length || !!this.advice.lunar);
  }

  /** Ce que disent déjà le message et les autres gestes (le temps qu'annonce leur signal) : la bande ne le répète pas. */
  get suppressedSignals(): WeatherCondition[] {
    const shown = this.choice ? [this.choice.head, ...this.choice.others] : [];
    return shown.map((m) => m?.announces ?? null).filter((c): c is WeatherCondition => !!c);
  }

  /** « Hors ligne, données de 7 h 40 » : l'heure dite comme dans la langue de l'app. */
  get offlineParams(): Record<string, string> | null {
    const since = this.dataStore.offlineSince();
    return since ? { time: formatVwDate(since, 'hour', this.storageService.getCurrentLanguage()) } : null;
  }

  /** Les gestes du jardin qui attendent, pour « Cette quinzaine ». */
  get tasksCount(): number {
    return this.todo?.tasks?.length ?? 0;
  }

  openWeather() {
    void this.router.navigate([routesConstants.WEATHER]);
  }

  openMoon() {
    void this.router.navigate([routesConstants.LUNAR]);
  }

  /**
   * Loads the user's in-progress plants and maps them to GDD milestone items.
   * Best-effort: a failure must not break the home page (widget self-hides when empty).
   */
  async loadGddMilestones() {
    try {
      // L'endpoint dédié plutôt que la liste des plantes : celle-ci ramenait
      // dix plantes entières — variété, étape, accumulation — pour dessiner
      // trois barres, et surtout elle ne portait PAS la date de récolte
      // estimée, qui n'atteignait donc jamais l'écran.
      const batch = await this.dataStore.get('garden/thermal-progress', () => this.plantApi.getThermalProgressBatch(), SNAPSHOT_ONLY);
      this.gddMilestones = (batch?.items ?? [])
        // Une plante suffit à figurer au suivi dès qu'elle a une étape en
        // cours : filtrer sur l'accumulation thermique vidait la section pour
        // tout un pan du jardin, celui qui n'en a pas.
        .filter((item) => !!item.currentStepName)
        .map((item): GddMilestoneItem => {
          const thermal = item.thermalProgress;
          // La même horloge que la fiche : le degré-jour quand il existe, la
          // durée de l'étape (`stepProgress`) sinon. La fin estimée est nulle
          // pour un compte gratuit : le serveur retire les jours restants.
          const clock = stepClock({ thermalProgress: thermal, currentGrowingStepProgression: item.stepProgress });
          return {
            plantUid: item.plantUid,
            cultivationUid: item.cultivationUid,
            variantUid: item.variantUid,
            cultivationName: this.label(item.cultivationName),
            variantName: this.label(item.variantName),
            locationName: item.locationName ?? '',
            currentStepName: this.label(item.currentStepName),
            progress: clock.progress,
            endDate: clock.endDate,
            // Pas de badge sans données thermiques, au calendrier, ni pour une étape finie qui attend sa saison.
            conditionBadge: clock.waitingForSeason ? null : displayedBadge(thermal?.conditionBadge),
            weatherGapDays: clock.weatherGapDays,
            waitingForSeason: clock.waitingForSeason,
            nextStepName: clock.waitingForSeason ? this.label(thermal?.nextStepName) : undefined,
            nextStepOpensOn: clock.waitingForSeason ? thermal?.nextStepOpensOn ?? null : null,
          };
        });
    } catch (e) {
      console.error('HomeComponent: failed to load GDD milestones', e);
    }
  }

  /** Un libellé du serveur dans la langue de l'app (le français à défaut). */
  private label(value: any): string {
    return labelText(value, this.storageService.getCurrentLanguage());
  }

  /**
   * Emits a profile click event to the parent component.
   */
  onProfileClick() {
    this.router.navigate([routesConstants.PROFILE]);
  }

  onAllArticles() {
    this.router.navigate([routesConstants.ARTICLES]);
  }
}
